// src/pages/Outbreaks.js

import React, { useEffect, useMemo, useState } from 'react';
import { CircleDot, Earth, MapPin, ShieldCheck } from 'lucide-react';
import TopBar from '../components/TopBar';
import authService from '../services/authService';
import { fetchPowerBISemanticData } from '../services/powerbiService';
import {
  VACCINE_PREVENTABLE_DISEASES,
  countActiveOutbreakCells,
  getDiseaseOutbreakItems,
  getOtherOutbreakDiseaseNames,
  getResolvedDiseaseOutbreakItems,
  isOutbreakEnded,
  normalizeOutbreakText,
  toOutbreakCountry
} from '../utils/outbreakMatching';
import '../modules/webv2/webV2Module.css';

const VACCINE_PREVENTABLE_DISEASE_LABELS = {
  cholera: 'Cholera',
  'yellow fever': 'Yellow fever',
  polio: 'Polio',
  measles: 'Measles',
  diphtheria: 'Diphtheria'
};
const WUENIC_COVERAGE_COLUMN_BY_DISEASE = {
  cholera: 'cholera_coverage_wuenic',
  'yellow fever': 'yellow_fever_coverage_wuenic',
  polio: 'polio_coverage_wuenic',
  measles: 'measles_coverage_wuenic',
  diphtheria: 'diphtheria_coverage_wuenic'
};
const OUTBREAK_MATRIX_INDICATORS = [
  { key: 'activeOutbreak', label: 'Active outbreak' },
  { key: 'cases', label: 'Number of cases' },
  { key: 'fatalities', label: 'Number of fatalities' },
  { key: 'wuenicCoverage', label: 'WUENIC coverage' },
  { key: 'resolvedOutbreaks', label: 'Resolved outbreaks (12 months)' }
];
const OUTBREAK_KPI_INDICATORS = OUTBREAK_MATRIX_INDICATORS.filter((indicator) => indicator.key !== 'resolvedOutbreaks');
const GIDEON_NEWS_LIMIT = 20;
const GIDEON_NEWS_DAYS = 7;
const GIDEON_ALERT_SINCE_DAYS = 2;
const GIDEON_VISIBLE_UPDATES = 2;
const GIDEON_VISIBLE_BULLETS = 2;
const GIDEON_BULLET_MAX_LENGTH = 180;
const GIDEON_SOURCE_URL = 'https://www.gideononline.com/';
const OUTBREAK_MATRIX_PREVIEW_ROWS = 8;
const OUTBREAK_TA_TEAM_NAME = 'Immunization - ADC';

const truncateText = (text = '', maxLength = GIDEON_BULLET_MAX_LENGTH) => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const getOutbreakBullets = (item) => {
  const bullets = Array.isArray(item?.bullets) && item.bullets.length
    ? item.bullets
    : [item?.text || ''];

  return bullets.filter(Boolean);
};

const getVisibleOutbreakBullets = (item, isExpanded) => {
  const bullets = getOutbreakBullets(item);

  if (isExpanded) return bullets;

  return bullets.slice(0, GIDEON_VISIBLE_BULLETS).map((bullet) => truncateText(bullet));
};

const getOutbreakItemKey = (item, idx) => `${item?.id || item?.reportUrl || 'gideon'}-${item?.title || 'update'}-${idx}`;

const getCountryNameFromRow = (row, fallback) => {
  const countryKey = Object.keys(row || {}).find((key) => /country|nation|location/i.test(key));
  return String(row?.[countryKey] || fallback).trim();
};

const isGaviCountryRow = (row) => String(row?.Gavi_Country || row?.gavi_country || '').trim().toLowerCase() === 'yes';

const getDiseaseCell = (countryRows, disease) => {
  const normalizedDisease = disease.toLowerCase();
  const directColumn = Object.keys(countryRows[0] || {}).find((key) =>
    key.toLowerCase().includes(normalizedDisease)
  );

  if (directColumn) {
    const value = countryRows
      .map((row) => row?.[directColumn])
      .find((item) => item !== null && item !== undefined && item !== '');

    if (value !== null && value !== undefined && value !== '') {
      return { status: 'present', label: String(value) };
    }
  }

  const hasMention = countryRows.some((row) =>
    Object.values(row || {}).some((value) => String(value || '').toLowerCase().includes(normalizedDisease))
  );

  return hasMention
    ? { status: 'mentioned', label: 'Mentioned' }
    : { status: 'empty', label: 'N/A' };
};

const formatMatrixValue = (value, suffix = '') => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 'N/A';
  return `${numericValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}${suffix}`;
};

const formatCompactMatrixValue = (value, suffix = '') => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  if (suffix === '%') return `${Math.round(numericValue)}%`;
  if (Math.abs(numericValue) >= 1000000) return `${(numericValue / 1000000).toFixed(1)}M`;
  if (Math.abs(numericValue) >= 1000) return `${Math.round(numericValue / 1000)}k`;
  return `${Math.round(numericValue)}`;
};

const getDiseaseCoverageValue = (source, disease) => {
  const column = WUENIC_COVERAGE_COLUMN_BY_DISEASE[disease];
  return column ? source?.[column] : null;
};

const getIndicatorForDisease = (countryName, disease, indicator, indicatorIndex, countryRows, outbreakItems, historicalOutbreakItems = []) => {
  const source = countryRows[0] || {};
  const matchingOutbreakItems = getDiseaseOutbreakItems(countryName, disease, outbreakItems, source.iso3);
  const isActive = matchingOutbreakItems.length > 0;
  const matrixStatus = isActive ? 'active' : 'no-data';

  if (indicator.key === 'activeOutbreak') {
    return {
      ...indicator,
      status: matrixStatus,
      value: isActive ? 'Active' : 'No active outbreak found',
      displayValue: isActive ? 'A' : '-'
    };
  }

  if (indicator.key === 'cases') {
    const totalCases = matchingOutbreakItems.reduce((sum, item) => {
      const cases = Number(item?.cases);
      return Number.isFinite(cases) ? sum + cases : sum;
    }, 0);

    return {
      ...indicator,
      status: matrixStatus,
      value: totalCases > 0 ? formatMatrixValue(totalCases) : 'No reported cases',
      displayValue: totalCases > 0 ? formatCompactMatrixValue(totalCases) : '-'
    };
  }

  if (indicator.key === 'fatalities') {
    const totalDeaths = matchingOutbreakItems.reduce((sum, item) => {
      const deaths = Number(item?.deaths);
      return Number.isFinite(deaths) ? sum + deaths : sum;
    }, 0);

    return {
      ...indicator,
      status: matrixStatus,
      value: totalDeaths > 0 ? formatMatrixValue(totalDeaths) : 'No reported fatalities',
      displayValue: totalDeaths > 0 ? formatCompactMatrixValue(totalDeaths) : '-'
    };
  }

  if (indicator.key === 'wuenicCoverage') {
    const diseaseCoverage = getDiseaseCoverageValue(source, disease);

    return {
      ...indicator,
      status: matrixStatus,
      value: formatMatrixValue(diseaseCoverage, '%'),
      displayValue: formatCompactMatrixValue(diseaseCoverage, '%')
    };
  }

  if (indicator.key === 'resolvedOutbreaks') {
    const resolvedItems = getResolvedDiseaseOutbreakItems(
      countryName,
      disease,
      historicalOutbreakItems,
      source.iso3,
      12,
      outbreakItems
    );
    const resolvedCount = resolvedItems.length;

    return {
      ...indicator,
      status: resolvedCount > 0 ? 'resolved' : 'no-data',
      value: resolvedCount > 0
        ? `${resolvedCount} resolved outbreak report${resolvedCount === 1 ? '' : 's'} in the last 12 months`
        : 'No resolved outbreak reports in the last 12 months',
      displayValue: resolvedCount > 0 ? String(resolvedCount) : '-'
    };
  }

  return {
    ...indicator,
    status: 'no-data',
    value: 'Placeholder',
    displayValue: '-'
  };
};

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};

const findColumnKey = (columns, names) => columns.find((col) => names.includes(normalizeColumn(col))) || null;
const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');
const getTaRequestKey = (row, index, titleColumnKey) => `${getRowText(row, titleColumnKey) || 'outbreak-ta-request'}-${index}`;
const isOutbreakTaTeam = (value) => normalizeOutbreakText(value).replace(/\s+/g, ' ').includes(normalizeOutbreakText(OUTBREAK_TA_TEAM_NAME));

const taRowMatchesSelectedCountry = (row, matchValues, taCountryColumnKey, taIso3ColumnKey) => {
  if (!matchValues.size) return false;

  const candidates = [
    getRowText(row, taCountryColumnKey),
    getRowText(row, taIso3ColumnKey)
  ]
    .map(normalizeOutbreakText)
    .filter(Boolean);

  return candidates.some((value) => matchValues.has(value));
};

const OutbreaksContent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gideonNewsItems, setGideonNewsItems] = useState([]);
  const [gideonAlertItems, setGideonAlertItems] = useState([]);
  const [gideonOtherAlertItems, setGideonOtherAlertItems] = useState([]);
  const [gideonHistoricalOutbreakItems, setGideonHistoricalOutbreakItems] = useState([]);
  const [gideonLoading, setGideonLoading] = useState(true);
  const [gideonError, setGideonError] = useState('');
  const [expandedOutbreakItems, setExpandedOutbreakItems] = useState({});
  const [countryTypeFilter, setCountryTypeFilter] = useState('gavi');
  const [showAllOutbreakMatrixCountries, setShowAllOutbreakMatrixCountries] = useState(false);
  const [selectedOutbreakCountryKey, setSelectedOutbreakCountryKey] = useState('');
  const [taRows, setTaRows] = useState([]);
  const [taColumns, setTaColumns] = useState([]);
  const [isTaLoading, setIsTaLoading] = useState(true);
  const [taError, setTaError] = useState('');
  const [expandedTaRequestKeys, setExpandedTaRequestKeys] = useState({});
  const taDatasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
  const taWorkspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
  const taDaxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
  const taTableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';

  const toggleOutbreakItem = (itemKey) => {
    setExpandedOutbreakItems((current) => ({
      ...current,
      [itemKey]: !current[itemKey]
    }));
  };

  useEffect(() => {
    const loadSummarizedIndicators = async () => {
      setLoading(true);
      setError('');

      try {
        const idToken = await authService.getIdToken();
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT
                si.iso3,
                ga.Country_Name,
                ga.Gavi_Country,
                AVG(
                  CASE
                    WHEN (LOWER(si.indicator) LIKE 'cholera%' OR LOWER(si.indicator) LIKE 'ocv%')
                      AND LOWER(si.indicator) LIKE '%wuenic%' THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE NULL
                  END
                ) AS cholera_coverage_wuenic,
                AVG(
                  CASE
                    WHEN (LOWER(si.indicator) LIKE 'yellow fever%' OR LOWER(si.indicator) LIKE 'yfv%')
                      AND LOWER(si.indicator) LIKE '%wuenic%' THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE NULL
                  END
                ) AS yellow_fever_coverage_wuenic,
                AVG(
                  CASE
                    WHEN si.indicator = 'Pol3 (2024) - WUENIC'
                      OR (LOWER(si.indicator) LIKE 'pol3%' AND LOWER(si.indicator) LIKE '%wuenic%')
                      OR (LOWER(si.indicator) LIKE 'polio%' AND LOWER(si.indicator) LIKE '%wuenic%') THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE NULL
                  END
                ) AS polio_coverage_wuenic,
                AVG(
                  CASE
                    WHEN si.indicator = 'MCV1 (2024) - WUENIC'
                      OR (LOWER(si.indicator) LIKE 'mcv1%' AND LOWER(si.indicator) LIKE '%wuenic%')
                      OR (LOWER(si.indicator) LIKE 'measles%' AND LOWER(si.indicator) LIKE '%wuenic%') THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE NULL
                  END
                ) AS measles_coverage_wuenic,
                AVG(
                  CASE
                    WHEN si.indicator = 'DTP3 (2024) - WUENIC' THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE NULL
                  END
                ) AS diphtheria_coverage_wuenic,
                SUM(
                  CASE
                    WHEN si.indicator = 'DTP1 (2024) - Surviving Infants UNPD' THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE 0
                  END
                ) AS dtp1_surviving_infants,
                SUM(
                  CASE
                    WHEN si.indicator = 'DTP1 (2024) - Children Vaccinated' THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE 0
                  END
                ) AS dtp1_children_vaccinated,
                GREATEST(
                  SUM(
                    CASE
                      WHEN si.indicator = 'DTP1 (2024) - Surviving Infants UNPD' THEN CAST(
                        NULLIF(
                          REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                          ''
                        ) AS DECIMAL(20, 3)
                      )
                      ELSE 0
                    END
                  ) -
                  SUM(
                    CASE
                      WHEN si.indicator = 'DTP1 (2024) - Children Vaccinated' THEN CAST(
                        NULLIF(
                          REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                          ''
                        ) AS DECIMAL(20, 3)
                      )
                      ELSE 0
                    END
                  ),
                  0
                ) AS zero_dose_children,
                MAX(
                  CASE
                    WHEN si.indicator = 'BCG (2024) - Children in Target'
                      OR (LOWER(si.indicator) LIKE 'bcg%' AND LOWER(si.indicator) LIKE '%children in target%')
                    THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE NULL
                  END
                ) AS bcg_children_target,
                MAX(
                  CASE
                    WHEN LOWER(si.indicator) IN ('total number of stores', 'number of stores')
                      OR LOWER(si.indicator) LIKE '%total%stores%'
                      OR LOWER(si.indicator) LIKE '%number%of%stores%'
                    THEN CAST(
                      NULLIF(
                        REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                        ''
                      ) AS DECIMAL(20, 3)
                    )
                    ELSE NULL
                  END
                ) AS total_stores,
                CASE
                  WHEN MAX(
                    CASE
                      WHEN LOWER(si.indicator) IN ('total number of stores', 'number of stores')
                        OR LOWER(si.indicator) LIKE '%total%stores%'
                        OR LOWER(si.indicator) LIKE '%number%of%stores%'
                      THEN CAST(
                        NULLIF(
                          REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                          ''
                        ) AS DECIMAL(20, 3)
                      )
                      ELSE NULL
                    END
                  ) > 0
                  THEN MAX(
                    CASE
                      WHEN si.indicator = 'BCG (2024) - Children in Target'
                        OR (LOWER(si.indicator) LIKE 'bcg%' AND LOWER(si.indicator) LIKE '%children in target%')
                      THEN CAST(
                        NULLIF(
                          REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                          ''
                        ) AS DECIMAL(20, 3)
                      )
                      ELSE NULL
                    END
                  ) / MAX(
                    CASE
                      WHEN LOWER(si.indicator) IN ('total number of stores', 'number of stores')
                        OR LOWER(si.indicator) LIKE '%total%stores%'
                        OR LOWER(si.indicator) LIKE '%number%of%stores%'
                      THEN CAST(
                        NULLIF(
                          REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(si.value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                          ''
                        ) AS DECIMAL(20, 3)
                      )
                      ELSE NULL
                    END
                  )
                  ELSE NULL
                END AS children_per_store
              FROM thrive_appdb_prd.summarized_indicators si
              LEFT JOIN thrive_appdb_prd.geo_admin ga
                ON ga.iso3 = si.iso3
              WHERE LOWER(TRIM(COALESCE(ga.thrive360_status, ''))) = 'reporting'
                AND (
                  si.indicator IN (
                    'DTP3 (2024) - WUENIC',
                    'Pol3 (2024) - WUENIC',
                    'MCV1 (2024) - WUENIC',
                    'DTP1 (2024) - Surviving Infants UNPD',
                    'DTP1 (2024) - Children Vaccinated',
                    'BCG (2024) - Children in Target'
                  )
                  OR ((LOWER(si.indicator) LIKE 'cholera%' OR LOWER(si.indicator) LIKE 'ocv%') AND LOWER(si.indicator) LIKE '%wuenic%')
                  OR ((LOWER(si.indicator) LIKE 'yellow fever%' OR LOWER(si.indicator) LIKE 'yfv%') AND LOWER(si.indicator) LIKE '%wuenic%')
                  OR ((LOWER(si.indicator) LIKE 'pol3%' OR LOWER(si.indicator) LIKE 'polio%') AND LOWER(si.indicator) LIKE '%wuenic%')
                  OR ((LOWER(si.indicator) LIKE 'mcv1%' OR LOWER(si.indicator) LIKE 'measles%') AND LOWER(si.indicator) LIKE '%wuenic%')
                  OR (LOWER(si.indicator) LIKE 'bcg%' AND LOWER(si.indicator) LIKE '%children in target%')
                  OR LOWER(si.indicator) IN ('total number of stores', 'number of stores')
                  OR LOWER(si.indicator) LIKE '%total%stores%'
                  OR LOWER(si.indicator) LIKE '%number%of%stores%'
                )
              GROUP BY si.iso3, ga.Country_Name, ga.Gavi_Country
              ORDER BY ga.Country_Name;
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const data = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
        setRows(data);
      } catch (err) {
        console.error('Failed to load summarized indicators:', err);
        setError(err?.message || 'Failed to load summarized indicators data.');
      } finally {
        setLoading(false);
      }
    };

    loadSummarizedIndicators();
  }, []);

  useEffect(() => {
    const loadGideonOutbreaks = async () => {
      setGideonLoading(true);
      setGideonError('');

      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
        const response = await fetch(`${apiBase}/gideon-outbreaks?count=${GIDEON_NEWS_LIMIT}&days=${GIDEON_NEWS_DAYS}&since=${GIDEON_ALERT_SINCE_DAYS}`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const newsItems = Array.isArray(payload?.newsItems) ? payload.newsItems : [];
        const alertItems = Array.isArray(payload?.alertItems) ? payload.alertItems : [];
        const otherAlertItems = Array.isArray(payload?.otherAlertItems) ? payload.otherAlertItems : [];
        const historicalOutbreakItems = Array.isArray(payload?.historicalOutbreakItems) ? payload.historicalOutbreakItems : [];

        setGideonNewsItems(newsItems);
        setGideonAlertItems(alertItems);
        setGideonOtherAlertItems(otherAlertItems);
        setGideonHistoricalOutbreakItems(historicalOutbreakItems);
      } catch (err) {
        console.error('Failed to load GIDEON outbreak reports:', err);
        setGideonNewsItems([]);
        setGideonAlertItems([]);
        setGideonOtherAlertItems([]);
        setGideonHistoricalOutbreakItems([]);
        setGideonError(err?.message || 'Failed to load GIDEON outbreak reports.');
      } finally {
        setGideonLoading(false);
      }
    };

    loadGideonOutbreaks();
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadTaRequests = async () => {
      setIsTaLoading(true);
      setTaError('');

      try {
        const effectiveQuery = String(taDaxQuery || '').trim() || undefined;
        const effectiveTableName = effectiveQuery ? undefined : (String(taTableName || '').trim() || undefined);
        const data = await fetchPowerBISemanticData(
          taDatasetId,
          taWorkspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );

        if (!mounted) return;
        setTaRows(Array.isArray(data?.data) ? data.data : []);
        setTaColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setTaRows([]);
        setTaColumns([]);
        setTaError('TA Hub requests are unavailable right now.');
      } finally {
        if (mounted) setIsTaLoading(false);
      }
    };

    loadTaRequests();

    return () => {
      mounted = false;
    };
  }, [taDatasetId, taWorkspaceId, taDaxQuery, taTableName]);

  const filteredRows = useMemo(() => (
    rows.filter((row) => {
      if (countryTypeFilter === 'gavi') return isGaviCountryRow(row);
      if (countryTypeFilter === 'non-gavi') return !isGaviCountryRow(row);
      return true;
    })
  ), [countryTypeFilter, rows]);

  const filteredCountryNames = useMemo(() => {
    const names = new Set();
    filteredRows.forEach((row, idx) => {
      const countryName = normalizeOutbreakText(getCountryNameFromRow(row, `Row ${idx + 1}`));
      if (countryName) names.add(countryName);
    });
    return names;
  }, [filteredRows]);

  const filteredGideonNewsItems = useMemo(() => {
    if (!filteredCountryNames.size) return [];
    return gideonNewsItems.filter((item) => filteredCountryNames.has(normalizeOutbreakText(item?.country)));
  }, [filteredCountryNames, gideonNewsItems]);

  const filteredGideonAlertItems = useMemo(() => {
    if (!filteredCountryNames.size) return [];
    return gideonAlertItems.filter((item) =>
      filteredCountryNames.has(normalizeOutbreakText(item?.country)) &&
      !isOutbreakEnded(item)
    );
  }, [filteredCountryNames, gideonAlertItems]);

  const filteredGideonOtherAlertItems = useMemo(() => {
    if (!filteredCountryNames.size) return [];
    return gideonOtherAlertItems.filter((item) =>
      filteredCountryNames.has(normalizeOutbreakText(item?.country)) &&
      !isOutbreakEnded(item)
    );
  }, [filteredCountryNames, gideonOtherAlertItems]);

  const visibleGideonNewsItems = useMemo(
    () => filteredGideonNewsItems.slice(0, GIDEON_VISIBLE_UPDATES),
    [filteredGideonNewsItems]
  );

  useEffect(() => {
    setShowAllOutbreakMatrixCountries(false);
  }, [countryTypeFilter]);

  const outbreakDiseaseMatrixRows = useMemo(() => {
    const groupedCountries = new Map();

    filteredRows.forEach((row, idx) => {
      const countryName = getCountryNameFromRow(row, `Row ${idx + 1}`);
      if (!countryName) return;

      if (!groupedCountries.has(countryName)) {
        groupedCountries.set(countryName, []);
      }

      groupedCountries.get(countryName).push(row);
    });

    return Array.from(groupedCountries.entries())
      .map(([countryName, countryRows]) => ({
        countryName,
        iso3: countryRows[0]?.iso3 || '',
        diseases: VACCINE_PREVENTABLE_DISEASES.reduce((acc, disease) => {
          acc[disease] = {
            ...getDiseaseCell(countryRows, disease),
            indicators: OUTBREAK_MATRIX_INDICATORS.map((indicator, indicatorIndex) =>
              getIndicatorForDisease(
                countryName,
                disease,
                indicator,
                indicatorIndex,
                countryRows,
                filteredGideonAlertItems,
                gideonHistoricalOutbreakItems
              )
            )
          };
          return acc;
        }, {}),
        otherOutbreakDiseases: getOtherOutbreakDiseaseNames(
          countryName,
          filteredGideonOtherAlertItems,
          countryRows[0]?.iso3 || ''
        )
      }))
      .sort((a, b) => {
        const aHasActiveOutbreak = VACCINE_PREVENTABLE_DISEASES.some((disease) =>
          a.diseases[disease]?.indicators?.[0]?.value === 'Active'
        );
        const bHasActiveOutbreak = VACCINE_PREVENTABLE_DISEASES.some((disease) =>
          b.diseases[disease]?.indicators?.[0]?.value === 'Active'
        );

        if (aHasActiveOutbreak !== bHasActiveOutbreak) return aHasActiveOutbreak ? -1 : 1;
        return a.countryName.localeCompare(b.countryName);
      });
  }, [filteredRows, filteredGideonAlertItems, filteredGideonOtherAlertItems, gideonHistoricalOutbreakItems]);

  const outbreakSummaryCards = useMemo(() => {
    const coverageValues = filteredRows
      .flatMap((row) =>
        VACCINE_PREVENTABLE_DISEASES.map((disease) =>
          Number(row?.[WUENIC_COVERAGE_COLUMN_BY_DISEASE[disease]])
        )
      )
      .filter(Number.isFinite);
    const averageCoverage = coverageValues.length
      ? coverageValues.reduce((sum, value) => sum + value, 0) / coverageValues.length
      : null;
    const totalGideonDeaths = filteredGideonAlertItems.reduce((sum, item) => {
      const deaths = Number(item?.deaths);
      return Number.isFinite(deaths) ? sum + deaths : sum;
    }, 0);
    const cardIcons = [Earth, ShieldCheck, CircleDot, MapPin];
    const activeOutbreakCount = countActiveOutbreakCells(
      gideonAlertItems,
      filteredRows.map((row, idx) => toOutbreakCountry(row, getCountryNameFromRow(row, `Row ${idx + 1}`)))
    );
    const totalGideonCases = filteredGideonAlertItems.reduce((sum, item) => {
      const cases = Number(item?.cases);
      return Number.isFinite(cases) ? sum + cases : sum;
    }, 0);

    return OUTBREAK_KPI_INDICATORS.map((indicator, idx) => {
      let value = 'N/A';
      let meta = 'To be connected';

      if (indicator.key === 'activeOutbreak') {
        value = gideonLoading ? 'Loading...' : activeOutbreakCount.toLocaleString();
        meta = 'Active country-disease outbreaks in the matrix';
      } else if (indicator.key === 'cases') {
        value = gideonLoading ? 'Loading...' : totalGideonCases.toLocaleString();
        meta = 'Reported cases from GIDEON alerts';
      } else if (indicator.key === 'fatalities') {
        value = gideonLoading ? 'Loading...' : totalGideonDeaths.toLocaleString();
        meta = 'Reported fatalities from GIDEON alerts';
      } else if (indicator.key === 'wuenicCoverage') {
        value = loading ? 'Loading...' : averageCoverage === null ? 'N/A' : `${Math.round(averageCoverage)}%`;
        meta = 'Average across reporting countries';
      } else {
        value = loading ? 'Loading...' : 'N/A';
      }

      return {
        key: indicator.key,
        title: indicator.label,
        value,
        meta,
        icon: cardIcons[idx]
      };
    });
  }, [filteredRows, loading, gideonLoading, filteredGideonAlertItems, gideonAlertItems]);

  const visibleOutbreakDiseaseMatrixRows = showAllOutbreakMatrixCountries
    ? outbreakDiseaseMatrixRows
    : outbreakDiseaseMatrixRows.slice(0, OUTBREAK_MATRIX_PREVIEW_ROWS);

  const selectedOutbreakCountry = useMemo(() => {
    return outbreakDiseaseMatrixRows.find((country) => country.countryName === selectedOutbreakCountryKey) ||
      outbreakDiseaseMatrixRows[0] ||
      null;
  }, [outbreakDiseaseMatrixRows, selectedOutbreakCountryKey]);

  const selectedActiveDiseaseRows = useMemo(() => {
    if (!selectedOutbreakCountry) return [];
    return VACCINE_PREVENTABLE_DISEASES
      .map((disease) => ({
        disease,
        cell: selectedOutbreakCountry.diseases[disease]
      }))
      .filter(({ cell }) => cell?.indicators?.[0]?.value === 'Active');
  }, [selectedOutbreakCountry]);

  const taLeadCoeTeamColumnKey = useMemo(
    () => findColumnKey(taColumns, ['lead coe team (accountable)', 'lead coe team', 'lead coe', 'coe team']),
    [taColumns]
  );
  const taCountryColumnKey = useMemo(
    () => findColumnKey(taColumns, ['country']),
    [taColumns]
  );
  const taIso3ColumnKey = useMemo(
    () => findColumnKey(taColumns, ['iso3', 'country iso3', 'country_iso3', 'country code', 'country iso']),
    [taColumns]
  );
  const taTitleColumnKey = useMemo(
    () => findColumnKey(taColumns, [
      'request title',
      'request',
      'title',
      'activity',
      'intervention',
      'action',
      'issue'
    ]),
    [taColumns]
  );
  const taDescriptionColumnKey = useMemo(
    () => findColumnKey(taColumns, ['description', 'request description']),
    [taColumns]
  );
  const taStatusColumnKey = useMemo(
    () => findColumnKey(taColumns, ['implementation status', 'status']),
    [taColumns]
  );
  const taLeadStaffColumnKey = useMemo(
    () => findColumnKey(taColumns, ['ta lead staff', 'ta lead staff (accountable)']),
    [taColumns]
  );
  const selectedCountryMatchValues = useMemo(() => {
    const values = new Set();
    if (!selectedOutbreakCountry) return values;
    const countryName = normalizeOutbreakText(selectedOutbreakCountry.countryName);
    const iso3 = normalizeOutbreakText(selectedOutbreakCountry.iso3);
    if (countryName) values.add(countryName);
    if (iso3) values.add(iso3);
    return values;
  }, [selectedOutbreakCountry]);
  const outbreakTaRequests = useMemo(() => {
    const teamRows = taRows.filter((row) => isOutbreakTaTeam(getRowText(row, taLeadCoeTeamColumnKey)));
    if (!selectedOutbreakCountry) return [];

    return teamRows.filter((row) => (
      taRowMatchesSelectedCountry(row, selectedCountryMatchValues, taCountryColumnKey, taIso3ColumnKey)
    ));
  }, [
    taRows,
    taLeadCoeTeamColumnKey,
    selectedOutbreakCountry,
    selectedCountryMatchValues,
    taCountryColumnKey,
    taIso3ColumnKey
  ]);

  const toggleTaRequestExpanded = (requestKey) => {
    setExpandedTaRequestKeys((current) => ({
      ...current,
      [requestKey]: !current[requestKey]
    }));
  };

  useEffect(() => {
    setExpandedTaRequestKeys({});
  }, [selectedOutbreakCountryKey]);

  useEffect(() => {
    if (!selectedOutbreakCountry) return;
    setSelectedOutbreakCountryKey(selectedOutbreakCountry.countryName);
  }, [selectedOutbreakCountry]);

  return (
    <section className="home-section vaccines-page-section outbreaks-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <p className="vaccines-section-label">Outbreak Monitoring</p>
          <h2 className="vaccines-dashboard-title">Outbreaks overview</h2>
          <p className="vaccines-dashboard-subtitle">
            Track active vaccine-preventable disease outbreaks, other country outbreaks, cases, fatalities, and WUENIC coverage.
          </p>
        </div>
      </div>

      <div className="vaccines-kpi-row outbreaks-kpi-row">
        {outbreakSummaryCards.map((card) => {
          const Icon = card.icon;

          return (
            <article key={card.key} className={`vaccines-kpi-card outbreaks-kpi-${card.key}`}>
              <span className="vaccines-kpi-icon" aria-hidden="true">
                <Icon />
              </span>
              <div>
                <p className="vaccines-kpi-value">{card.value}</p>
                <p className="vaccines-kpi-label">{card.title}</p>
                <p className="vaccines-kpi-meta">{card.meta}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="vaccines-schedule-card outbreaks-matrix-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Disease x Country Matrix</p>
            <h3 className="vaccines-card-title">Vaccine-preventable diseases by country</h3>
          </div>
          <div className="vaccines-matrix-actions">
            <div className="vaccines-segmented-control" aria-label="Country funding filter">
              <button
                type="button"
                className={countryTypeFilter === 'gavi' ? 'is-active' : ''}
                onClick={() => setCountryTypeFilter('gavi')}
              >
                Gavi Countries
              </button>
              <button
                type="button"
                className={countryTypeFilter === 'non-gavi' ? 'is-active' : ''}
                onClick={() => setCountryTypeFilter('non-gavi')}
              >
                Non-Gavi Countries
              </button>
              <button
                type="button"
                className={countryTypeFilter === 'all' ? 'is-active' : ''}
                onClick={() => setCountryTypeFilter('all')}
              >
                All
              </button>
            </div>
          </div>
        </div>

        <div className="vaccines-matrix-wrap outbreaks-matrix-wrap">
          <table className="vaccines-matrix-table outbreaks-matrix-table" aria-label="Vaccine-preventable disease country matrix">
            <thead>
              <tr>
                <th scope="col">Country</th>
                {VACCINE_PREVENTABLE_DISEASES.map((disease) => (
                  <th key={disease} scope="col">{VACCINE_PREVENTABLE_DISEASE_LABELS[disease]}</th>
                ))}
                <th scope="col">Other Outbreaks</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={VACCINE_PREVENTABLE_DISEASES.length + 2}>Loading disease matrix...</td>
                </tr>
              )}
              {!loading && outbreakDiseaseMatrixRows.length === 0 && (
                <tr>
                  <td colSpan={VACCINE_PREVENTABLE_DISEASES.length + 2}>No country data found for the disease matrix.</td>
                </tr>
              )}
              {!loading && visibleOutbreakDiseaseMatrixRows.map((country) => (
                <tr
                  key={country.countryName}
                  className={country.countryName === selectedOutbreakCountryKey ? 'is-selected' : ''}
                  aria-selected={country.countryName === selectedOutbreakCountryKey}
                >
                  <th scope="row">
                    <button type="button" onClick={() => setSelectedOutbreakCountryKey(country.countryName)}>
                      {country.countryName}
                    </button>
                  </th>
                  {VACCINE_PREVENTABLE_DISEASES.map((disease) => {
                    const cell = country.diseases[disease];

                    return (
                      <td key={`${country.countryName}-${disease}`}>
                        <div className="outbreaks-disease-indicator-pack">
                          {cell.indicators.map((indicator) => (
                            <span
                              key={`${country.countryName}-${disease}-${indicator.key}`}
                              className={`outbreaks-indicator-square is-${indicator.status}`}
                              data-tooltip-title={indicator.label}
                              data-tooltip={`${country.countryName} · ${VACCINE_PREVENTABLE_DISEASE_LABELS[disease]}: ${indicator.value}`}
                              aria-label={`${country.countryName} - ${VACCINE_PREVENTABLE_DISEASE_LABELS[disease]} - ${indicator.label}: ${indicator.value}`}
                            >
                              {indicator.displayValue}
                            </span>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                  <td className="outbreaks-other-outbreaks-cell">
                    {country.otherOutbreakDiseases.length ? (
                      <div className="outbreaks-other-diseases">
                        {country.otherOutbreakDiseases.map((diseaseName) => (
                          <span
                            key={`${country.countryName}-other-${diseaseName}`}
                            className="outbreaks-other-disease-pill"
                            title={diseaseName}
                          >
                            {diseaseName}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="outbreaks-other-diseases is-empty">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {outbreakDiseaseMatrixRows.length > OUTBREAK_MATRIX_PREVIEW_ROWS && (
          <div className="vaccines-matrix-footer">
            <p className="vaccines-footnote">
              Showing {visibleOutbreakDiseaseMatrixRows.length} of {outbreakDiseaseMatrixRows.length} countries.
            </p>
            <button
              type="button"
              className="vaccines-expand-button"
              onClick={() => setShowAllOutbreakMatrixCountries((current) => !current)}
            >
              {showAllOutbreakMatrixCountries ? 'Show fewer countries' : `Show all ${outbreakDiseaseMatrixRows.length} countries`}
            </button>
          </div>
        )}
        <p className="vaccines-footnote outbreaks-source-footnote">
          Source: GIDEON{' '}
          <a href={GIDEON_SOURCE_URL} target="_blank" rel="noopener noreferrer">
            {GIDEON_SOURCE_URL}
          </a>
        </p>
      </div>

      <div className="vaccines-schedule-card outbreaks-ecdc-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">GIDEON Outbreak News</p>
            <h3 className="vaccines-card-title">Vaccine-preventable disease updates</h3>
          </div>
          <p className="budgets-data-note">
            {gideonLoading || loading ? 'Loading GIDEON updates...' : `${visibleGideonNewsItems.length} updates`}
          </p>
        </div>

        {gideonError && <p className="budgets-error-note">{gideonError}</p>}

        {!gideonLoading && !loading && !gideonError && visibleGideonNewsItems.length === 0 && (
          <p className="outbreaks-ecdc-empty">
            No cholera, yellow fever, polio, measles or diphtheria updates found in the latest GIDEON outbreak news.
          </p>
        )}

        <div className="outbreaks-ecdc-grid">
          {visibleGideonNewsItems.map((item, idx) => {
            const itemKey = getOutbreakItemKey(item, idx);
            const isExpanded = Boolean(expandedOutbreakItems[itemKey]);
            const allBullets = getOutbreakBullets(item);

            return (
              <article
                key={itemKey}
                className={`outbreaks-ecdc-item ${isExpanded ? 'is-expanded' : ''}`}
              >
                <div className="outbreaks-ecdc-item-header">
                  <h4>{item.title}</h4>
                  <span>{item.reportDate || item.publicationDate || 'GIDEON'}</span>
                </div>
                <ul>
                  {getVisibleOutbreakBullets(item, isExpanded).map((bullet, bulletIdx) => (
                    <li key={`${item.title}-${bulletIdx}`}>{bullet}</li>
                  ))}
                </ul>
                {allBullets.length > GIDEON_VISIBLE_BULLETS && (
                  <button
                    type="button"
                    className="outbreaks-ecdc-toggle"
                    onClick={() => toggleOutbreakItem(itemKey)}
                  >
                    {isExpanded ? 'Show less' : 'Read More'}
                  </button>
                )}
                {item.reportUrl && (
                  <a href={item.reportUrl} target="_blank" rel="noopener noreferrer">
                    View GIDEON source
                  </a>
                )}
              </article>
            );
          })}
        </div>
      </div>

      <div className="vaccines-schedule-card outbreaks-data-card">
        <div className="vaccines-country-header">
          <div>
            <p className="vaccines-section-label">Country snapshot</p>
            <h3>{selectedOutbreakCountry?.countryName || 'No country selected'}</h3>
          </div>
          {selectedOutbreakCountry?.iso3 && (
            <span className="country-badge outbreaks-country-code">{selectedOutbreakCountry.iso3}</span>
          )}
        </div>

        {!loading && error && <p className="budgets-error-note">{error}</p>}
        {loading && <p className="outbreaks-ecdc-empty">Loading country snapshot...</p>}
        {!loading && !selectedOutbreakCountry && (
          <p className="outbreaks-ecdc-empty">Select a country in the matrix to view its outbreak snapshot.</p>
        )}

        {selectedOutbreakCountry && (
          <div className="vaccines-panel-section outbreaks-country-diseases">
            <h4>Vaccine-preventable disease indicators</h4>
            {selectedActiveDiseaseRows.length === 0 ? (
              <p className="outbreaks-ecdc-empty">No active outbreaks for this country.</p>
            ) : (
              <div className="vaccines-wuenic-table-wrap outbreaks-snapshot-table-wrap">
                <table className="vaccines-wuenic-table outbreaks-snapshot-table" aria-label="Country disease indicator details">
                  <thead>
                    <tr>
                      <th scope="col">Disease</th>
                      {OUTBREAK_MATRIX_INDICATORS.map((indicator) => (
                        <th key={indicator.key} scope="col">{indicator.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedActiveDiseaseRows.map(({ disease, cell }) => (
                      <tr key={`${selectedOutbreakCountry.countryName}-${disease}`}>
                        <th scope="row">{VACCINE_PREVENTABLE_DISEASE_LABELS[disease]}</th>
                        {cell.indicators.map((indicator) => (
                          <td key={`${selectedOutbreakCountry.countryName}-${disease}-${indicator.key}`}>
                            {indicator.value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="vaccines-schedule-card budgets-ta-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Targeted TA</p>
            <h3 className="vaccines-card-title">Immunization - ADC TA Hub requests</h3>
          </div>
          <p className="budgets-data-note">
            {isTaLoading
              ? 'Loading TA Hub requests...'
              : !selectedOutbreakCountry
                ? 'Select a country in the matrix above'
                : `${outbreakTaRequests.length} request${outbreakTaRequests.length === 1 ? '' : 's'} for ${selectedOutbreakCountry.countryName}`}
          </p>
        </div>

        {taError && <p className="budgets-error-note">{taError}</p>}
        {!isTaLoading && !taError && !selectedOutbreakCountry && (
          <p className="budgets-data-note">Click a country in the matrix above to view Immunization - ADC TA requests for that country.</p>
        )}

        <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
          <table className="vaccines-wuenic-table budgets-table budgets-ta-table" aria-label="Immunization - ADC TA Hub requests">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">Description</th>
                <th scope="col">Status</th>
                <th scope="col">TA Lead Staff</th>
                <th scope="col">Lead CoE Team</th>
              </tr>
            </thead>
            <tbody>
              {isTaLoading && (
                <tr>
                  <td colSpan="5">Loading Immunization - ADC TA requests...</td>
                </tr>
              )}
              {!isTaLoading && !taError && selectedOutbreakCountry && outbreakTaRequests.length === 0 && (
                <tr>
                  <td colSpan="5">No Immunization - ADC TA requests found for {selectedOutbreakCountry.countryName}.</td>
                </tr>
              )}
              {!isTaLoading && outbreakTaRequests.map((row, index) => {
                const requestKey = getTaRequestKey(row, index, taTitleColumnKey);
                const description = getRowText(row, taDescriptionColumnKey) || getRowText(row, taTitleColumnKey);
                const isExpanded = Boolean(expandedTaRequestKeys[requestKey]);
                const shouldTruncate = description.length > 200;
                const visibleDescription = shouldTruncate && !isExpanded
                  ? `${description.slice(0, 200).trimEnd()}...`
                  : description;

                return (
                  <tr key={requestKey}>
                    <td>{getRowText(row, taCountryColumnKey) || 'N/A'}</td>
                    <td>
                      {visibleDescription || 'N/A'}
                      {shouldTruncate && (
                        <button
                          type="button"
                          className="budgets-ta-read-more-button"
                          onClick={() => toggleTaRequestExpanded(requestKey)}
                        >
                          {isExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </td>
                    <td>{getRowText(row, taStatusColumnKey) || 'N/A'}</td>
                    <td>{getRowText(row, taLeadStaffColumnKey) || 'N/A'}</td>
                    <td>
                      <span className="budgets-ta-priority">{getRowText(row, taLeadCoeTeamColumnKey) || OUTBREAK_TA_TEAM_NAME}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const Outbreaks = ({ userClaims, isEmbedded = false }) => {
  if (isEmbedded) {
    return <OutbreaksContent />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="outbreaks" userClaims={userClaims} />
      <div className="v2-module p-4">
        <OutbreaksContent />
      </div>
    </div>
  );
};

export default Outbreaks; 